/**
 * Auth Constants
 * Centralized configuration for authentication
 */

// Canonical auth routes (EN as source of truth)
export const AUTH_ROUTES = {
  LOGIN: '/en/auth/login',
  SIGNUP: '/en/auth/signup',
  FORGOT_PASSWORD: '/en/auth/forgot-password',
  RESET_PASSWORD: '/en/auth/reset-password',
  VERIFY_EMAIL: '/en/auth/verify-email',
  INVITE: '/en/auth/invite',
  MFA: '/en/auth/mfa',
  LOGOUT: '/en/auth/logout',
  CALLBACK: '/en/auth/callback',
  ERROR: '/en/auth/error',
  
  // Legacy routes (for redirects)
  LEGACY_AUTH: '/en/auth',
  LEGACY_SIGN_IN: '/en/auth/sign-in',
  LEGACY_SIGN_UP: '/en/auth/sign-up',
  LEGACY_ACCEPT_INVITE: '/en/auth/accept-invite',
  PORTAL_LOGIN: '/portal/login',
  PORTAL_REGISTER: '/portal/register',
} as const;

// Post-auth destinations
export const POST_AUTH_ROUTES = {
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_ONBOARDING: '/admin/onboarding',
  AGENT_DASHBOARD: '/en/agent',
  CLIENT_PORTAL: '/en/portal',
  PROFILE: '/profile',
} as const;

// Error pages
export const ERROR_ROUTES = {
  UNAUTHORIZED: '/unauthorized',
  ACCOUNT_DISABLED: '/account-disabled',
  NOT_FOUND: '/404',
} as const;

// Auth error messages (generic to prevent enumeration)
export const AUTH_ERRORS = {
  INVALID_CREDENTIALS: 'Invalid email or password. Please try again.',
  EMAIL_NOT_CONFIRMED: 'Please confirm your email address before signing in.',
  EMAIL_ALREADY_EXISTS: 'An account with this email already exists.',
  ACCOUNT_DISABLED: 'Your account has been disabled. Please contact support.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  INVALID_TOKEN: 'This link is invalid or has expired.',
  RATE_LIMITED: 'Too many attempts. Please try again later.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNEXPECTED_ERROR: 'An unexpected error occurred. Please try again.',
  OAUTH_CANCELLED: 'Sign in was cancelled.',
  OAUTH_ERROR: 'Error signing in with this provider. Please try again.',
  OAUTH_STATE_MISMATCH: 'Security validation failed. Please try again.',
  EMAIL_NOT_VERIFIED_GOOGLE: 'Your Google account email is not verified.',
  MFA_REQUIRED: 'Multi-factor authentication is required.',
  MFA_INVALID_CODE: 'Invalid verification code.',
  PASSWORD_TOO_WEAK: 'Password does not meet security requirements.',
} as const;

// Session configuration
export const SESSION_CONFIG = {
  REFRESH_THRESHOLD_SECONDS: 60 * 5, // Refresh if less than 5 minutes left
  IDLE_TIMEOUT_MINUTES: 30,
  ABSOLUTE_TIMEOUT_HOURS: 24,
} as const;

// Rate limiting
export const RATE_LIMITS = {
  LOGIN_ATTEMPTS_MAX: 5,
  LOGIN_ATTEMPTS_WINDOW_MINUTES: 15,
  PASSWORD_RESET_MAX: 3,
  PASSWORD_RESET_WINDOW_MINUTES: 60,
  SIGNUP_MAX: 3,
  SIGNUP_WINDOW_MINUTES: 60,
} as const;

// OAuth providers
export const OAUTH_PROVIDERS = {
  GOOGLE: 'google',
  APPLE: 'apple',
} as const;

export type OAuthProvider = typeof OAUTH_PROVIDERS[keyof typeof OAUTH_PROVIDERS];
